'use client';

import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo, ReactNode } from 'react';
import { supabase } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useAdvisorConnections, AdvisorConnection, AdvisorRole } from '@/hooks/useAdvisorConnections';
import { trackSupabaseError } from '@/lib/analytics/errorHandler';
import { logger } from '@/lib/logger';

interface ClientProfile {
  id: string;
  fullName: string | null;
  email: string | null;
  state: string | null;
  marriageDate: string | null;
  separationDate: string | null;
  intakeComplete: boolean;
  goalsComplete: boolean;
  // Case details
  caseNumber: string | null;
  courtName: string | null;
  courtJurisdiction: string | null;
  judgeName: string | null;
  caseFilingDate: string | null;
  // Opposing counsel
  opposingCounselName: string | null;
  opposingCounselFirm: string | null;
  opposingCounselEmail: string | null;
  opposingCounselPhone: string | null;
}

interface AdvisorContextType {
  // Mode detection
  isAdvisorMode: boolean;

  // Client connections (as an advisor)
  clientConnections: AdvisorConnection[];
  isLoadingClients: boolean;

  // Selected client
  selectedClientId: string | null;
  selectedClient: ClientProfile | null;
  selectedConnection: AdvisorConnection | null;
  isLoadingClient: boolean;

  // Actions
  selectClient: (clientId: string) => void;
  clearClient: () => void;

  // Current role for the selected client
  currentRole: AdvisorRole | null;

  // Permission checks
  canViewStrategy: boolean;
  canViewDocuments: boolean;
  canViewChildren: boolean;
  canViewAllFinancials: boolean;
  canViewGoals: boolean;
  canViewSupportCalculations: boolean;
  canAddNotes: boolean;
}

const AdvisorContext = createContext<AdvisorContextType | undefined>(undefined);

// Role-based permissions — exported for unit testing
export const ROLE_PERMISSIONS: Record<AdvisorRole, {
  viewStrategy: boolean;
  viewDocuments: boolean;
  viewChildren: boolean;
  viewAllFinancials: boolean;
  viewGoals: boolean;
  viewSupportCalculations: boolean;
  addNotes: boolean;
}> = {
  lead_attorney: {
    viewStrategy: true,
    viewDocuments: true,
    viewChildren: true,
    viewAllFinancials: true,
    viewGoals: true,
    viewSupportCalculations: true,
    addNotes: true,
  },
  associate_attorney: {
    viewStrategy: true,
    viewDocuments: true,
    viewChildren: true,
    viewAllFinancials: true,
    viewGoals: true,
    viewSupportCalculations: true,
    addNotes: true,
  },
  paralegal: {
    viewStrategy: false,
    viewDocuments: true,
    viewChildren: false,
    viewAllFinancials: true,
    viewGoals: false,
    viewSupportCalculations: true,
    addNotes: true,
  },
  cpa: {
    viewStrategy: true,
    viewDocuments: true,
    viewChildren: false,
    viewAllFinancials: true,
    viewGoals: true,
    viewSupportCalculations: true,
    addNotes: true,
  },
  financial_advisor: {
    viewStrategy: true,
    viewDocuments: true,
    viewChildren: false,
    viewAllFinancials: true,
    viewGoals: true,
    viewSupportCalculations: true,
    addNotes: true,
  },
  mediator: {
    viewStrategy: false,
    viewDocuments: true,
    viewChildren: true,
    viewAllFinancials: true,
    viewGoals: true,
    viewSupportCalculations: false,
    addNotes: false,
  },
  expert_witness: {
    viewStrategy: false,
    viewDocuments: true,
    viewChildren: false,
    viewAllFinancials: true,
    viewGoals: false,
    viewSupportCalculations: false,
    addNotes: true,
  },
  family_member: {
    viewStrategy: false,
    viewDocuments: false,
    viewChildren: false,
    viewAllFinancials: false,
    viewGoals: false,
    viewSupportCalculations: false,
    addNotes: false,
  },
};

export function AdvisorProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { clientConnections, isLoadingClients, isAdvisor } = useAdvisorConnections();

  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [selectedClient, setSelectedClient] = useState<ClientProfile | null>(null);
  const [isLoadingClient, setIsLoadingClient] = useState(false);

  // Keep a ref to clientConnections for stable callback
  const clientConnectionsRef = useRef(clientConnections);
  clientConnectionsRef.current = clientConnections;

  // Memoize connection lookup to avoid reference changes
  const selectedConnection = useMemo(() => {
    return clientConnections.find(c => c.clientUserId === selectedClientId) || null;
  }, [clientConnections, selectedClientId]);

  // Memoize role to avoid reference changes
  const currentRole = useMemo(() => {
    return selectedConnection?.role || null;
  }, [selectedConnection]);

  // Memoize permissions based on role
  const permissions = useMemo(() => {
    return currentRole ? ROLE_PERMISSIONS[currentRole] : null;
  }, [currentRole]);

  // Fetch client profile when selected
  useEffect(() => {
    if (!selectedClientId || !user) {
      setSelectedClient(null);
      return;
    }

    const fetchClientProfile = async () => {
      setIsLoadingClient(true);
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select(`
            id, full_name, email, state, intake_complete, goals_complete,
            date_of_marriage, date_of_separation,
            case_number, court_name, court_jurisdiction, judge_name, case_filing_date,
            opposing_counsel_name, opposing_counsel_firm, opposing_counsel_email, opposing_counsel_phone
          `)
          .eq('id', selectedClientId)
          .maybeSingle();

        if (error) {
          logger.error('[AdvisorContext] Error fetching client profile:', error);
          trackSupabaseError('select', 'profiles', { message: error.message, code: error.code });
          setSelectedClient(null);
          return;
        }

        // Profile not found - client may not have completed signup yet
        if (!data) {
          logger.log('[AdvisorContext] Client profile not found, may be pending signup');
          setSelectedClient(null);
          return;
        }

        const profileData = data as any;
        setSelectedClient({
          id: profileData.id,
          fullName: profileData.full_name,
          email: profileData.email,
          state: profileData.state,
          marriageDate: profileData.date_of_marriage || null,
          separationDate: profileData.date_of_separation || null,
          intakeComplete: profileData.intake_complete || false,
          goalsComplete: profileData.goals_complete || false,
          // Case details
          caseNumber: profileData.case_number || null,
          courtName: profileData.court_name || null,
          courtJurisdiction: profileData.court_jurisdiction || null,
          judgeName: profileData.judge_name || null,
          caseFilingDate: profileData.case_filing_date || null,
          // Opposing counsel
          opposingCounselName: profileData.opposing_counsel_name || null,
          opposingCounselFirm: profileData.opposing_counsel_firm || null,
          opposingCounselEmail: profileData.opposing_counsel_email || null,
          opposingCounselPhone: profileData.opposing_counsel_phone || null,
        });
      } catch (err) {
        logger.error('[AdvisorContext] Error:', err);
        trackSupabaseError('select', 'profiles', { message: err instanceof Error ? err.message : 'Unknown error' });
        setSelectedClient(null);
      } finally {
        setIsLoadingClient(false);
      }
    };

    fetchClientProfile();
  }, [selectedClientId, user]);

  // Auto-select first client if only one
  useEffect(() => {
    if (clientConnections.length === 1 && !selectedClientId) {
      setSelectedClientId(clientConnections[0].clientUserId);
    }
  }, [clientConnections, selectedClientId]);

  const selectClient = useCallback((clientId: string) => {
    const connection = clientConnectionsRef.current.find(c => c.clientUserId === clientId);
    if (connection) {
      setSelectedClientId(clientId);
    }
  }, []);

  const clearClient = useCallback(() => {
    setSelectedClientId(null);
    setSelectedClient(null);
  }, []);

  const value = useMemo<AdvisorContextType>(() => ({
    isAdvisorMode: isAdvisor,
    clientConnections,
    isLoadingClients,
    selectedClientId,
    selectedClient,
    selectedConnection,
    isLoadingClient,
    selectClient,
    clearClient,
    currentRole,
    canViewStrategy: permissions?.viewStrategy ?? false,
    canViewDocuments: permissions?.viewDocuments ?? false,
    canViewChildren: permissions?.viewChildren ?? false,
    canViewAllFinancials: permissions?.viewAllFinancials ?? false,
    canViewGoals: permissions?.viewGoals ?? false,
    canViewSupportCalculations: permissions?.viewSupportCalculations ?? false,
    canAddNotes: permissions?.addNotes ?? false,
  }), [
    isAdvisor,
    clientConnections,
    isLoadingClients,
    selectedClientId,
    selectedClient,
    selectedConnection,
    isLoadingClient,
    selectClient,
    clearClient,
    currentRole,
    permissions,
  ]);

  return (
    <AdvisorContext.Provider value={value}>
      {children}
    </AdvisorContext.Provider>
  );
}

/**
 * Default advisor context (client-mode / no advisor) returned when a component
 * renders outside AdvisorProvider. Shared by useAdvisor and useAdvisorSafe.
 */
type AdvisorContextDefaults = Pick<
  AdvisorContextType,
  | 'isAdvisorMode' | 'clientConnections' | 'isLoadingClients' | 'selectedClientId' | 'selectedClient'
  | 'selectedConnection' | 'isLoadingClient' | 'selectClient' | 'clearClient' | 'currentRole'
  | 'canViewStrategy' | 'canViewDocuments' | 'canViewChildren' | 'canViewAllFinancials' | 'canViewGoals'
  | 'canViewSupportCalculations' | 'canAddNotes'
>;
const ADVISOR_CONTEXT_DEFAULTS: AdvisorContextDefaults = {
  isAdvisorMode: false,
  clientConnections: [],
  isLoadingClients: true,
  selectedClientId: null,
  selectedClient: null,
  selectedConnection: null,
  isLoadingClient: false,
  selectClient: () => {},
  clearClient: () => {},
  currentRole: null,
  canViewStrategy: false,
  canViewDocuments: false,
  canViewChildren: false,
  canViewAllFinancials: false,
  canViewGoals: false,
  canViewSupportCalculations: false,
  canAddNotes: false,
};

export function useAdvisor() {
  const context = useContext(AdvisorContext);
  if (context === undefined) {
    // Previously threw — but that surfaced as production route crashes
    // (Bugsnag #1362) when an advisor-aware component/hook rendered outside the
    // provider (e.g. shared discovery/interrogatory features under the client
    // AppLayout). Degrade gracefully to client-mode defaults instead of crashing.
    logger.error('[useAdvisor] Called outside AdvisorProvider — returning client-mode defaults');
    return ADVISOR_CONTEXT_DEFAULTS;
  }
  return context;
}

/**
 * Safe version of useAdvisor that returns default values when called outside AdvisorProvider.
 * Use this in components that may render before the provider is mounted (e.g., during navigation).
 */
export function useAdvisorSafe() {
  const context = useContext(AdvisorContext);

  if (context === undefined) {
    // Return safe defaults when context is unavailable
    // This prevents crashes during navigation race conditions or error recovery
    logger.warn('[useAdvisorSafe] Called outside AdvisorProvider, returning defaults');
    return { ...ADVISOR_CONTEXT_DEFAULTS, isContextAvailable: false };
  }

  return { ...context, isContextAvailable: true as const };
}

// Hook for fetching client-specific data as an advisor
export function useClientData<T>(
  tableName: string,
  options: {
    select?: string;
    orderBy?: string;
    ascending?: boolean;
  } = {}
) {
  // Use safe version to prevent crashes during navigation or error recovery
  const { selectedClientId, isAdvisorMode } = useAdvisorSafe();
  const { user } = useAuth();
  const [data, setData] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const clientId = isAdvisorMode ? selectedClientId : user?.id;

  useEffect(() => {
    if (!clientId) {
      setData([]);
      setIsLoading(false);
      return;
    }

    const fetchData = async () => {
      setIsLoading(true);
      try {
        let query = (supabase as any)
          .from(tableName)
          .select(options.select || '*')
          .eq('user_id', clientId);

        if (options.orderBy) {
          query = query.order(options.orderBy, { ascending: options.ascending ?? false });
        }

        const { data: result, error: fetchError } = await query;

        if (fetchError) {
          throw fetchError;
        }

        setData(result as T[]);
        setError(null);
      } catch (err) {
        logger.error(`[useClientData] Error fetching ${tableName}:`, err);
        trackSupabaseError('select', tableName, { message: err instanceof Error ? err.message : 'Unknown error' });
        setError(err as Error);
        setData([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [clientId, tableName, options.select, options.orderBy, options.ascending]);

  return { data, isLoading, error, clientId };
}
